import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { SalesChartProps } from '@/types/props/components';
import SalesChartTooltip from './salesChartTooltip';
import DefaultButton from '@/components/common/default/DefaultButton';
import clsx from 'clsx';
import { dataFormatter } from '@/utilites/sku/sku.misc.utils';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';

const SalesChart: React.FC<SalesChartProps> = ({
  chartData,
  period,
  setPeriod,
}) => {
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const { t: tSku } = useTranslation('sku');

  if (chartData) {
    return (
      <div className="tw-relative tw-border-2 tw-border-solid tw-border-customGray tw-px-4 tw-rounded-md">
        <div className="tw-absolute tw-right-0 tw-top-0 tw-w-2/4 tw-flex tw-items-center tw-justify-end tw-mb-2 child:tw-p-3 tw-border-2 tw-border-solid tw-border-b-customGray tw-border-r-customGray tw-border-l-customGray tw-border-t-0 tw-rounded-md child:tw-font-semibold child:tw-basis-1/5">
          <DefaultButton
            onClick={() => setPeriod('Week')}
            className={clsx(
              'tw-rounded-bl-md',
              'hover:tw-bg-main hover:tw-text-white',
              'active:tw-bg-blue-700',
              'focus:tw-ring-2 focus:tw-ring-main',
              'disabled:tw-bg-gray-300 disabled:tw-text-gray-500',
              'tw-transition tw-duration-200 tw-ease-in-out',
              period === 'Week'
                ? 'tw-bg-main tw-text-white'
                : 'tw-bg-white tw-text-textBlack'
            )}
            disabled={false} // Cambiar a true para probar el estado disabled
          >
            {translate(CommonEnum['sales_chart.periods.1W'])}
          </DefaultButton>
          <DefaultButton
            onClick={() => setPeriod('Month')}
            className={clsx(
              'hover:tw-bg-main hover:tw-text-white',
              'active:tw-bg-blue-700',
              'focus:tw-ring-2 focus:tw-ring-main',
              'disabled:tw-bg-gray-300 disabled:tw-text-gray-500',
              'tw-transition tw-duration-200 tw-ease-in-out',
              period === 'Month'
                ? 'tw-bg-main tw-text-white'
                : 'tw-bg-white tw-text-textBlack'
            )}
            disabled={false}
          >
            {translate(CommonEnum['sales_chart.periods.1M'])}
          </DefaultButton>
          <DefaultButton
            onClick={() => setPeriod('6M')}
            className={clsx(
              'hover:tw-bg-main hover:tw-text-white',
              'active:tw-bg-blue-700',
              'focus:tw-ring-2 focus:tw-ring-main',
              'disabled:tw-bg-gray-300 disabled:tw-text-gray-500',
              'tw-transition tw-duration-200 tw-ease-in-out',
              period === '6M'
                ? 'tw-bg-main tw-text-white'
                : 'tw-bg-white tw-text-textBlack'
            )}
            disabled={false}
          >
            {translate(CommonEnum['sales_chart.periods.6M'])}
          </DefaultButton>
          <DefaultButton
            onClick={() => setPeriod('1Y')}
            className={clsx(
              'hover:tw-bg-main hover:tw-text-white',
              'active:tw-bg-blue-700',
              'focus:tw-ring-2 focus:tw-ring-main',
              'disabled:tw-bg-gray-300 disabled:tw-text-gray-500',
              'tw-transition tw-duration-200 tw-ease-in-out',
              period === '1Y'
                ? 'tw-bg-main tw-text-white'
                : 'tw-bg-white tw-text-textBlack'
            )}
            disabled={false}
          >
            {translate(CommonEnum['sales_chart.periods.1Y'])}
          </DefaultButton>
          <DefaultButton
            onClick={() => setPeriod('All')}
            className={clsx(
              'tw-rounded-br-md',
              'hover:tw-bg-main hover:tw-text-white',
              'active:tw-bg-blue-700',
              'focus:tw-ring-2 focus:tw-ring-main',
              'disabled:tw-bg-gray-300 disabled:tw-text-gray-500',
              'tw-transition tw-duration-200 tw-ease-in-out',
              period === 'All'
                ? 'tw-bg-main tw-text-white'
                : 'tw-bg-white tw-text-textBlack'
            )}
            disabled={false}
          >
            {translate(CommonEnum['sales_chart.periods.MAX'])}
          </DefaultButton>
        </div>
        <div className="tw-py-8"></div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            width={500}
            height={300}
            data={chartData}
            margin={{ top: 10, right: 10, left: 20, bottom: 20 }}
          >
            <CartesianGrid
              horizontal={true}
              vertical={false}
              stroke="#ccc"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, dx: 5 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={dataFormatter}
              tick={{ fill: '#6D7175', fontSize: 12, dx: -10 }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              filterNull
              content={
                <SalesChartTooltip active={false} payload={[]} label={''} />
              }
            />
            <Legend />
            <Line
              type="monotone"
              name={translationFallback(
                'value.market_value',
                tSku('value.market_value'),
                'Market value'
              )}
              dataKey="mv"
              stroke="#497DEF"
              strokeWidth={3}
              activeDot={{ r: 8 }}
              dot={false}
              fill="url(#lineGradient)"
            />
            <Line
              name={translationFallback(
                'value.sales',
                tSku('value.sales'),
                'Sales'
              )}
              type="monotone"
              dataKey="sv"
              stroke="#C2AD7D"
              dot={{ stroke: '#C2AD7D', fill: '#C2AD7D', r: 6 }}
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return null;
};

export default SalesChart;
